/**
 * useManagementTeamFeed — Floating Chat relay conversation feed.
 *
 * Scoped to the relay conversation only:
 * - Author is "relay" or "travis" (human lead)
 * - OR message is tagged "relay-response"
 *
 * All other authors and dev-agent posts are excluded.
 * Returns messages ordered oldest-first for chat display.
 */
import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  limit as fbLimit,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { subscribeWithReconnect } from "../lib/firestoreReconnect";
import { useRosterLeadId } from "./useRosterLeadId";

const PAGE_SIZE = 100;

// System authors that may be missing workspace_id due to server bug (seq #11200)
const SYSTEM_AUTHORS = ["relay", "advisor", "claude-code"];

// Relay conversation participants — include messages from these authors.
// track:browser-session-notification-identity — "travis" and the legacy
// email stay permanently (historical scrollback: whoever held the lead
// role when a past message was posted should still show up in this feed),
// with the *current* resolved roster lead added alongside rather than
// replacing them, so a lead rotation doesn't retroactively hide anyone's
// real history or exclude the new lead's fresh messages.
const RELAY_AUTHORS = new Set(["relay", "travis", "travisbernritter@gmail.com"]);

export interface ManagementTeamMessage {
  id: string;
  author: string;
  content: string;
  tags: string[];
  timestamp: string;
  sequence: number;
  workspace_id?: string;
  linkedIssue?: string;
  replyToSequence?: number;
}

export interface ManagementTeamFeedState {
  messages: ManagementTeamMessage[];
  loading: boolean;
  hasMore: boolean;
}

export interface ManagementTeamFeedOptions {
  /** track:track-backlog--perf-defer-floatingchat-listeners-to-open — skip the listener entirely until true */
  enabled?: boolean;
  pageSize?: number;
}

/**
 * Include if author is a relay conversation participant (the static
 * historical set, or the current roster lead) OR message tagged
 * relay-response.
 */
function shouldInclude(msg: ManagementTeamMessage, currentLeadId: string): boolean {
  if (RELAY_AUTHORS.has(msg.author)) return true;
  if (msg.author === currentLeadId) return true;
  if (msg.tags?.includes("relay-response")) return true;
  return false;
}

export function useManagementTeamFeed(workspaceId: string, options?: ManagementTeamFeedOptions): ManagementTeamFeedState {
  const enabled = options?.enabled ?? true;
  const pageSize = options?.pageSize ?? PAGE_SIZE;
  const rosterLeadId = useRosterLeadId(workspaceId);
  const [messages, setMessages] = useState<ManagementTeamMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const ref = collection(db, "team_log_messages");
    const q = query(
      ref,
      where("workspace_id", "==", workspaceId),
      orderBy("sequence", "desc"),
      fbLimit(pageSize)
    );

    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        (snap) => {
          markConnected();
          setHasMore(snap.size >= pageSize);
          const all: ManagementTeamMessage[] = snap.docs
            .map((d) => {
              const data = d.data();

              // Seq #11200 — workspace_id fallback for system authors
              const effectiveWorkspaceId = data.workspace_id ||
                (SYSTEM_AUTHORS.includes(data.author) ? workspaceId : null);

              if (!effectiveWorkspaceId || effectiveWorkspaceId !== workspaceId) {
                return null;
              }

              return {
                id: d.id,
                author: data.author || "unknown",
                content: data.content || "",
                tags: Array.isArray(data.tags) ? data.tags : [],
                timestamp: data.timestamp?.toDate?.()?.toISOString() || new Date().toISOString(),
                sequence: data.sequence || 0,
                workspace_id: effectiveWorkspaceId,
                linkedIssue: data.linkedIssue,
                replyToSequence: data.replyToSequence,
              };
            })
            .filter((msg) => msg !== null) as ManagementTeamMessage[];

          const filtered = all.filter((msg) => shouldInclude(msg, rosterLeadId));
          setMessages(filtered);
          setLoading(false);
        },
        (err) => {
          console.warn("[ManagementTeamFeed] listener failed:", err);
          setLoading(false);
          retry();
        }
      )
    );
  }, [workspaceId, enabled, pageSize, rosterLeadId]);

  return { messages, loading, hasMore };
}

export const __TEST_ONLY__ = { shouldInclude };
